import { useMemo, useRef, useState } from 'react';
import { ArrowDown, ArrowUp, AlertTriangle, Pencil, Plus, Trash2 } from 'lucide-react';
import {
  SETTING_LIST_CODE_PATTERN,
  SETTING_LIST_ITEM_TYPES,
  duplicateSettingListValues,
  readSettingListItem,
  settingListExtraFields,
  settingListItemTypeSpec,
  settingListValueProblem,
  suggestSettingListValue,
  writeSettingListItem,
  type AppSettingList,
  type AppSettingListItemType,
  type SettingListItemDraft,
} from '@peles/shared';
import { EntityModal } from '../../common/EntityModal';
import { SearchableListSelect } from '../../common/SearchableListSelect';
import { SettingToggle } from '../SettingToggle';
import { useDialog } from '../../../context/DialogContext';
import { useCreateAppSettingList, useUpdateAppSettingList } from '../../../hooks/useAppSettings';
import { extractApiErrorMessage } from '../../../utils/apiError';
import { SettingListItemModal } from './SettingListItemModal';

export interface SettingListModalProps {
  /** The list edited; null creates one. */
  list: AppSettingList | null;
  /** Codes other lists already have. */
  existingCodes: readonly string[];
  onClose: () => void;
}

interface Row {
  key: number;
  draft: SettingListItemDraft;
}

const FIELD =
  'w-full min-h-11 sm:min-h-0 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500';
const ICON_BUTTON =
  'inline-flex items-center justify-center min-h-11 min-w-11 sm:min-h-8 sm:min-w-8 rounded text-gray-400 hover:text-gray-700 hover:bg-gray-100 disabled:opacity-30 disabled:hover:bg-transparent';

/** One list's details and items, edited in place and saved together. */
export function SettingListModal({ list, existingCodes, onClose }: SettingListModalProps) {
  const isNew = list === null;
  const { confirm } = useDialog();
  const createList = useCreateAppSettingList();
  const updateList = useUpdateAppSettingList();

  const nextKey = useRef(0);
  const toRows = (items: unknown[]): Row[] =>
    items.map((raw) => ({ key: nextKey.current++, draft: readSettingListItem(raw) }));

  const [name, setName] = useState(list?.name ?? '');
  const [listCode, setListCode] = useState(list?.listCode ?? '');
  const [codeTouched, setCodeTouched] = useState(!isNew);
  const [description, setDescription] = useState(list?.description ?? '');
  const [itemType, setItemType] = useState<AppSettingListItemType>(
    settingListItemTypeSpec(list?.itemType ?? 'TEXT').type,
  );
  const [isActive, setIsActive] = useState(list?.isActive ?? true);
  const [rows, setRows] = useState<Row[]>(() =>
    toRows(Array.isArray(list?.items) ? list.items : []),
  );
  const [dirty, setDirty] = useState(false);
  const [editing, setEditing] = useState<{ index: number | null } | null>(null);
  const [tried, setTried] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const extraFields = settingListExtraFields(listCode);
  const touch = () => {
    setDirty(true);
    setSaveError(null);
  };

  const drafts = useMemo(() => rows.map((r) => r.draft), [rows]);
  const duplicates = useMemo(() => duplicateSettingListValues(drafts), [drafts]);
  const rowProblems = useMemo(
    () =>
      drafts.map(
        (d, i) =>
          settingListValueProblem(itemType, d.value) ??
          (duplicates.has(i) ? 'Another item has this value' : null),
      ),
    [drafts, itemType, duplicates],
  );
  const flagged = rowProblems.filter(Boolean).length;

  const codeTaken = isNew && existingCodes.includes(listCode.trim());
  const codeProblem = !isNew
    ? null
    : !listCode.trim()
      ? 'Enter a code'
      : !SETTING_LIST_CODE_PATTERN.test(listCode.trim())
        ? 'Use lower-case letters, digits and underscores, starting with a letter'
        : codeTaken
          ? 'Another list already has this code'
          : null;
  const problem =
    (!name.trim() ? 'Enter a name' : null) ??
    codeProblem ??
    (flagged ? `Fix the ${flagged === 1 ? 'flagged item' : `${flagged} flagged items`}` : null);

  async function close() {
    if (
      dirty &&
      !(await confirm({
        title: 'Discard changes?',
        message: 'Your changes to this list have not been saved.',
        confirmLabel: 'Discard',
      }))
    )
      return;
    onClose();
  }

  async function changeType(next: AppSettingListItemType) {
    if (next === itemType) return;
    if (
      rows.length > 0 &&
      !(await confirm({
        variant: 'warning',
        title: 'Change the item type?',
        message:
          'Items keep the values they have. Any that do not fit the new type are flagged to fix before saving.',
        confirmLabel: 'Change type',
      }))
    )
      return;
    setItemType(next);
    touch();
  }

  function move(index: number, by: -1 | 1) {
    setRows((rs) => {
      const to = index + by;
      if (to < 0 || to >= rs.length) return rs;
      const next = [...rs];
      [next[index], next[to]] = [next[to], next[index]];
      return next;
    });
    touch();
  }

  function remove(index: number) {
    setRows((rs) => rs.filter((_, i) => i !== index));
    touch();
  }

  function saveItem(draft: SettingListItemDraft) {
    const index = editing?.index ?? null;
    setRows((rs) =>
      index === null
        ? [...rs, { key: nextKey.current++, draft }]
        : rs.map((r, i) => (i === index ? { ...r, draft } : r)),
    );
    setEditing(null);
    touch();
  }

  async function save() {
    setTried(true);
    if (problem) return;
    const items = drafts.map((d) => writeSettingListItem(d, itemType));
    try {
      if (isNew) {
        await createList.mutateAsync({
          listCode: listCode.trim(),
          name: name.trim(),
          itemType,
          ...(description.trim() && { description: description.trim() }),
          items,
          isActive,
        });
      } else {
        await updateList.mutateAsync({
          id: list.id,
          updates: {
            name: name.trim(),
            description: description.trim() || null,
            itemType,
            items,
            isActive,
          },
        });
      }
      onClose();
    } catch (err) {
      setSaveError(extractApiErrorMessage(err, 'The list could not be saved.'));
    }
  }

  const saving = createList.isPending || updateList.isPending;
  const editingDraft = editing?.index != null ? (rows[editing.index]?.draft ?? null) : null;
  const otherDrafts = drafts.filter((_, i) => i !== editing?.index);
  const otherValues = otherDrafts.map((d) => d.value);
  const otherLabels = otherDrafts.map((d) => d.label || d.value);

  return (
    <>
      <EntityModal
        open
        onClose={close}
        title={isNew ? 'New list' : `Edit ${list.name}`}
        size="md"
        footer={
          <div className="flex flex-wrap items-center justify-end gap-2">
            {tried && problem && (
              <p role="alert" className="mr-auto text-sm text-red-600">
                {problem}
              </p>
            )}
            {saveError && (
              <p role="alert" className="mr-auto text-sm text-red-600">
                {saveError}
              </p>
            )}
            <button
              type="button"
              onClick={close}
              className="min-h-11 sm:min-h-0 px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={save}
              disabled={saving}
              className="min-h-11 sm:min-h-0 px-4 py-2 text-sm text-white bg-primary-600 rounded-md hover:bg-primary-700 disabled:opacity-50"
            >
              {saving ? 'Saving…' : isNew ? 'Create list' : 'Save'}
            </button>
          </div>
        }
      >
        <div className="space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="list-name" className="block text-sm font-medium text-gray-700 mb-1">
                Name
              </label>
              <input
                id="list-name"
                type="text"
                value={name}
                autoFocus={isNew}
                onChange={(e) => {
                  setName(e.target.value);
                  if (isNew && !codeTouched) setListCode(suggestSettingListValue(e.target.value));
                  touch();
                }}
                className={FIELD}
              />
            </div>
            <div>
              <label htmlFor="list-code" className="block text-sm font-medium text-gray-700 mb-1">
                Code
              </label>
              {isNew ? (
                <input
                  id="list-code"
                  type="text"
                  value={listCode}
                  onChange={(e) => {
                    setListCode(e.target.value);
                    setCodeTouched(true);
                    touch();
                  }}
                  aria-invalid={(tried && !!codeProblem) || undefined}
                  className={`${FIELD} font-mono`}
                />
              ) : (
                <p
                  id="list-code"
                  className="px-3 py-2 rounded-md bg-gray-50 border border-gray-200 text-sm font-mono text-gray-700"
                >
                  {listCode}
                </p>
              )}
              <p className="mt-1 text-xs text-gray-500">
                {isNew
                  ? 'How the app finds the list. Cannot change later.'
                  : 'How the app finds the list.'}
              </p>
            </div>
          </div>

          <div>
            <label
              htmlFor="list-description"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Description
            </label>
            <textarea
              id="list-description"
              rows={2}
              value={description}
              onChange={(e) => {
                setDescription(e.target.value);
                touch();
              }}
              className={FIELD}
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-end">
            <div>
              <label
                htmlFor="list-item-type"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Item type
              </label>
              <SearchableListSelect
                inputId="list-item-type"
                ariaLabel="Item type"
                items={[...SETTING_LIST_ITEM_TYPES]}
                value={itemType}
                onChange={(v) => {
                  const next = (typeof v === 'string' ? v : v[0]) as AppSettingListItemType;
                  if (next) void changeType(next);
                }}
                getValue={(s) => s.type}
                getLabel={(s) => s.label}
                getSubtitle={(s) => s.description}
                clearable={false}
              />
            </div>
            <div className="flex items-center justify-between sm:justify-start gap-3 min-h-11">
              <span className="text-sm font-medium text-gray-700">Active</span>
              <SettingToggle
                checked={isActive}
                onChange={(next) => {
                  setIsActive(next);
                  touch();
                }}
                label={isActive ? 'Deactivate list' : 'Activate list'}
              />
            </div>
          </div>

          <section aria-labelledby="list-items-heading">
            <div className="flex items-center justify-between mb-2">
              <h3 id="list-items-heading" className="text-sm font-semibold text-gray-900">
                Items <span className="font-normal text-gray-500">({rows.length})</span>
              </h3>
              <button
                type="button"
                onClick={() => setEditing({ index: null })}
                className="inline-flex items-center gap-1 min-h-11 sm:min-h-0 px-3 py-1.5 text-sm text-primary-700 border border-primary-200 rounded-md hover:bg-primary-50"
              >
                <Plus size={14} />
                Add item
              </button>
            </div>
            {rows.length === 0 ? (
              <div className="px-4 py-6 text-center text-sm text-gray-500 border border-dashed border-gray-300 rounded-md">
                No items yet. Add the first one.
              </div>
            ) : (
              <ul className="divide-y divide-gray-100 border border-gray-200 rounded-md">
                {rows.map((row, i) => {
                  const d = row.draft;
                  const rowProblem = rowProblems[i];
                  return (
                    <li key={row.key} className="flex items-center gap-2 px-2 py-1.5">
                      <div className="flex flex-col sm:flex-row">
                        <button
                          type="button"
                          onClick={() => move(i, -1)}
                          disabled={i === 0}
                          className={ICON_BUTTON}
                          aria-label={`Move ${d.label || d.value} up`}
                        >
                          <ArrowUp size={14} />
                        </button>
                        <button
                          type="button"
                          onClick={() => move(i, 1)}
                          disabled={i === rows.length - 1}
                          className={ICON_BUTTON}
                          aria-label={`Move ${d.label || d.value} down`}
                        >
                          <ArrowDown size={14} />
                        </button>
                      </div>
                      {itemType === 'COLOR' && (
                        <span
                          className="h-5 w-5 shrink-0 rounded border border-gray-300"
                          style={{ backgroundColor: d.value }}
                          aria-hidden
                        />
                      )}
                      <button
                        type="button"
                        onClick={() => setEditing({ index: i })}
                        className="min-w-0 flex-1 text-left py-1"
                      >
                        <span className="block text-sm text-gray-900 truncate">
                          {d.label || d.value || <em className="text-gray-400">No value</em>}
                        </span>
                        <span className="flex flex-wrap items-center gap-1.5 text-xs text-gray-500">
                          {d.value && d.value !== d.label && (
                            <span className="font-mono truncate">{d.value}</span>
                          )}
                          {extraFields.map((f) => {
                            const v = d.extras[f.key];
                            const shown =
                              f.kind === 'boolean'
                                ? v === true
                                  ? f.label
                                  : null
                                : (f.options?.find((o) => o.value === v)?.label ?? null);
                            return shown ? (
                              <span
                                key={f.key}
                                className="px-1.5 py-0.5 rounded bg-gray-100 text-gray-600"
                              >
                                {shown}
                              </span>
                            ) : null;
                          })}
                        </span>
                        {rowProblem && (
                          <span className="flex items-center gap-1 text-xs text-amber-700">
                            <AlertTriangle size={12} aria-hidden />
                            {rowProblem}
                          </span>
                        )}
                      </button>
                      <button
                        type="button"
                        onClick={() => setEditing({ index: i })}
                        className={ICON_BUTTON}
                        aria-label={`Edit ${d.label || d.value}`}
                      >
                        <Pencil size={14} />
                      </button>
                      <button
                        type="button"
                        onClick={() => remove(i)}
                        className={`${ICON_BUTTON} hover:text-red-600`}
                        aria-label={`Remove ${d.label || d.value}`}
                      >
                        <Trash2 size={14} />
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
        </div>
      </EntityModal>

      {editing && (
        <SettingListItemModal
          open
          listCode={listCode}
          itemType={itemType}
          item={editingDraft}
          otherValues={otherValues}
          otherLabels={otherLabels}
          onSave={saveItem}
          onClose={() => setEditing(null)}
        />
      )}
    </>
  );
}
